import type { ReactNode } from "react";
import { Link } from "react-router";
import { AlertTriangle, type LucideIcon } from "lucide-react";
import { Button, Icon } from "../ui";

/** A part replaced by a newer one (PRD 5.4). Gold: information, not a failure. */
export function SupersessionNotice({ replacement, inStock }: { replacement: { id: number; sku: string; name: string }; inStock: boolean }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-gold-300 bg-surface-tint-gold p-4">
      <Icon icon={AlertTriangle} size={20} className="mt-0.5 text-gold-700" />
      <div>
        <p className="mb-0 type-body-strong text-gold-700">
          Replaced by{" "}
          <Link to={`/listings/${replacement.id}`} className="type-sku">
            {replacement.sku}
          </Link>{" "}
          · {replacement.name}
        </p>
        <p className="mt-1 mb-0 type-small text-ink-600">
          {inStock ? "We still have this one, so you can order either." : "This one is out of stock. Order the replacement instead."}
        </p>
      </div>
    </div>
  );
}

/** Nothing to show: says why, and where to go instead. */
export function EmptyState({ icon, title, children, action }: { icon: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-border-default bg-surface-card px-6 py-10 text-center">
      <Icon icon={icon} size={32} className="mx-auto mb-4 text-ink-300" />
      <h2 className="mb-2 type-h3 [font-size:var(--text-lg)]">{title}</h2>
      {children && <p className="mx-auto mb-0 max-w-[480px] type-body text-text-muted">{children}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/** A request that failed. Offers the one thing that might help. */
export function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <EmptyState icon={AlertTriangle} title="That didn't load" action={<Button variant="outline" onClick={onRetry}>Try again</Button>}>
      Check your connection, then try again.
    </EmptyState>
  );
}
